// aortaSlot.ts
import type { Editor } from '@tiptap/core';

/**
 * The aorta sentence's two size slots.
 *
 * The sentence carries a dropdown per structure:
 *
 *   Aorta: | [Normal] aortic root and [normal] ascending aorta size for gender and BSA.
 *
 * Both are real `span[data-editable]` markup in the template, which is what makes them
 * appear in the template editor and its preview alongside every other dropdown.
 *
 * The backfill below is the LEGACY path, for reports saved from the template before it
 * was patched. It marks the root adjective only, and adds a MARK ONLY — no text is
 * inserted, moved or reworded, so a signed report reads identically before and after.
 * The ascending slot cannot be backfilled: its adjective does not exist in the old
 * sentence, and inserting a word into an already-signed report is not something a
 * document viewer should do.
 */

/** Span class = dropdown key. `aorta-normal` predates the split and stays the root's. */
export const AORTA_SLOT = 'aorta-normal';
export const AORTA_ASCENDING_SLOT = 'aorta-ascending';

/**
 * Fallback options, used only for a clinic whose own list has no entry for a slot.
 *
 * Options normally come from the clinic, because the wording is theirs. A seeded row
 * wins over these — see `useClinicDropdowns`.
 */
export const AORTA_SLOT_OPTIONS = [
  'Normal',
  'Mildly dilated',
  'Moderately dilated',
  'Severely dilated',
];

/** Lower case: this slot prints mid-sentence, before "ascending aorta". */
export const AORTA_ASCENDING_OPTIONS = [
  'normal',
  'mildly dilated',
  'moderately dilated',
  'severely dilated',
];

/**
 * The adjective at the head of the aorta sentence, in an UNPATCHED report.
 *
 * Deliberately narrow: it must match this sentence and nothing else, so it requires the
 * leading adjective AND both structures named in the same text node. Covers both template
 * wordings ("for gender and BSA", "for BSA") and a sentence already reworded to a dilated
 * grade. A patched sentence never matches — its adjective is already a span, so the text
 * node no longer starts with it.
 */
const AORTA_SENTENCE =
  /^(\s*)(normal|mildly dilated|moderately dilated|severely dilated|dilated)(\s+aortic root\b[^.]*\bascending aorta\b)/i;

/**
 * Wrap that adjective in an `editableWord` mark so it opens the phrase picker.
 *
 * Idempotent, and a no-op on a report built from a patched template. Returns how many
 * were marked (0 or 1).
 */
export function backfillAortaSlot(editor: Editor): number {
  if (!editor) {
    return 0;
  }
  const markType = editor.state.schema.marks.editableWord;
  if (!markType) {
    return 0;
  }

  const { state } = editor;
  const { tr } = state;
  let marked = 0;

  state.doc.descendants((node, pos) => {
    if (!node.isText || !node.text || marked > 0) {
      return;
    }
    if (node.marks.some(m => m.type.name === 'editableWord')) {
      return; // already a slot — nothing to do
    }
    const found = AORTA_SENTENCE.exec(node.text);
    if (!found) {
      return;
    }
    const from = pos + found[1].length;
    tr.addMark(from, from + found[2].length, markType.create({ class: AORTA_SLOT }));
    marked += 1;
  });

  if (marked > 0) {
    // No history entry: a clinician's first undo should reverse their own edit, not this.
    tr.setMeta('addToHistory', false);
    editor.view.dispatch(tr);
  }
  return marked;
}
